import type { RestoreAccount } from '@repo/api-gw-sdk';

import { getRestoreNodeRoleArn } from '@/utils/accounts';

const principalsArray = (providerAccountIds: string[]) =>
  providerAccountIds
    .map((id) => `\n        "arn:aws:iam::${id}:root"`)
    .join(',');

const arnLikeArray = (providerAccountIds: string[]) =>
  providerAccountIds
    .map((id) => `\n          "arn:aws:iam::${id}:role/Eon*"`)
    .join(',');

const exactArnLikeArray = (arns: string[]) =>
  arns.map((arn) => `\n          "${arn}"`).join(',');

export const getKmsKeyPolicyStatements = (
  scanningAccountIds: string[],
  awsRestoreAccounts: RestoreAccount[]
) => {
  if (scanningAccountIds.length === 0) {
    return '';
  }

  const scanningPrincipals = principalsArray(scanningAccountIds);
  const scanningArnLike = arnLikeArray(scanningAccountIds);

  const scanningPolicy = `
  {
    "Sid": "Allow key usage in scanning account",
    "Effect": "Allow",
    "Principal": {
      "AWS": [${scanningPrincipals}
      ]
    },
    "Action": [
      "kms:DescribeKey",
      "kms:Encrypt",
      "kms:Decrypt",
      "kms:ReEncrypt*",
      "kms:GenerateDataKey*"
    ],
    "Resource": "*",
    "Condition": {
      "ArnLike": {
        "aws:PrincipalArn": [${scanningArnLike}
        ]
      }
    }
  },
  {
    "Sid": "Allow grant for snapshot copy in scanning account",
    "Effect": "Allow",
    "Principal": {
      "AWS": [${scanningPrincipals}
      ]
    },
    "Action": "kms:CreateGrant",
    "Resource": "*",
    "Condition": {
      "Bool": {
        "kms:GrantIsForAWSResource": "true"
      },
      "ArnLike": {
        "aws:PrincipalArn": [${scanningArnLike}
        ]
      }
    }
  }`;

  // Customers may rename their restore role, so the role ARN is not guaranteed to
  // match the default "Eon*" prefix. Use the actual roleArn from each restore
  // account so the suggested key policy stays valid for custom role names (EON-13430).
  // Derive the principal and ArnLike condition from the SAME filtered set so an
  // account missing a roleArn is never listed as a principal without a matching
  // condition (which would read as permissive but silently deny).
  const restoreEntries = awsRestoreAccounts.filter(
    (account) => !!account.restoreAccountAttributes.aws?.roleArn
  );

  if (restoreEntries.length === 0) {
    return scanningPolicy;
  }

  const restorePrincipals = principalsArray(
    restoreEntries.map((e) => e.providerAccountId)
  );
  const restoreArnLike = exactArnLikeArray(
    restoreEntries.map((e) => e.restoreAccountAttributes.aws!.roleArn)
  );
  // Full restores decrypt as the node role, whose deployed name can be <account role>Node or EonRestoreNodeRole.
  const restoreUsageArnLike = exactArnLikeArray(
    restoreEntries.flatMap((account) => [
      account.restoreAccountAttributes.aws!.roleArn,
      `${account.restoreAccountAttributes.aws!.roleArn}Node`,
      getRestoreNodeRoleArn(account),
    ])
  );

  const restorePolicy = `,
  {
    "Sid": "Allow key usage for restore node role and db query",
    "Effect": "Allow",
    "Principal": {
      "AWS": [${restorePrincipals}
      ]
    },
    "Action": [
      "kms:DescribeKey",
      "kms:Encrypt",
      "kms:Decrypt",
      "kms:ReEncrypt*",
      "kms:GenerateDataKey*"
    ],
    "Resource": "*",
    "Condition": {
      "ArnLike": {
        "aws:PrincipalArn": [${restoreUsageArnLike}
        ]
      }
    }
  },
  {
    "Sid": "Allow grant for snapshot restore",
    "Effect": "Allow",
    "Principal": {
      "AWS": [${restorePrincipals}
      ]
    },
    "Action": "kms:CreateGrant",
    "Resource": "*",
    "Condition": {
      "Bool": {
        "kms:GrantIsForAWSResource": "true"
      },
      "ArnLike": {
        "aws:PrincipalArn": [${restoreArnLike}
        ]
      }
    }
  }`;

  return `${scanningPolicy}${restorePolicy}`;
};
